import { FC, Fragment, useMemo, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { CheckCircleIcon, ExclamationTriangleIcon, ExclamationCircleIcon, ArrowPathIcon, BellIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import toast, { Toast, useToaster } from 'react-hot-toast';
import { classNames, wrapClick } from 'utils';
import { Avatar } from 'components';
import { useAuthStore, clearAuth } from 'state/auth';
import lodash from 'lodash';

const Toaster: FC = () => {
  const { toasts, handlers } = useToaster();
  const { startPause, endPause } = handlers;
  const hasLogout = useMemo(
    () =>
      lodash
        .chain(toasts)
        .filter(['id', 'logout'])
        .filter(toast => toast.visible)
        .value().length > 0,
    [toasts],
  );

  return (
    <>
      <Transition
        show={hasLogout}
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </Transition>
      <div
        ref={useRef(null)}
        onMouseEnter={startPause}
        onMouseLeave={endPause}
        aria-live="assertive"
        className={classNames('fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-50')}
      >
        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          {hasLogout
            ? lodash
                .chain(toasts)
                .filter(['id', 'logout'])
                .filter(toast => toast.visible)
                .map(toast => (
                  <Transition.Root key={toast.id} show={true} as={Fragment}>
                    <div className="max-w-sm w-full">
                      <Transition.Child
                        as={Fragment}
                        enter="transform ease-out duration-300 transition"
                        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="max-w-sm w-full ">
                          <LogoutToaster key={toast.id} toast={toast} />
                        </div>
                      </Transition.Child>
                    </div>
                  </Transition.Root>
                ))
                .value()
            : lodash
                .chain(toasts)
                .filter(toast => toast.visible)
                .reject(['id', 'logout'])
                .map(toast => (
                  <Transition
                    key={toast.id}
                    show={true}
                    as={Fragment}
                    enter="transform ease-out duration-300 transition"
                    enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                    enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="max-w-sm w-full">
                      <AlertToaster key={toast.id} toast={toast} />
                    </div>
                  </Transition>
                ))
                .value()}
        </div>
      </div>
    </>
  );
};

export default Toaster;

interface ToastComponentProps {
  toast: Toast;
}

const AlertToaster: FC<ToastComponentProps> = ({ toast: _toast }) => {
  const _toastMessage = JSON.parse(_toast.message as string);

  return (
    <div
      {..._toast.ariaProps}
      className="max-w-sm w-full bg-white dark:bg-gray-800 shadow-lg rounded pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
    >
      <div className="p-4">
        <div className={classNames(_toastMessage.description ? 'items-start' : 'items-center', 'flex')}>
          <div className="flex-shrink-0">
            {_toastMessage.type === 'success' && <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />}
            {_toastMessage.type === 'error' && <ExclamationCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />}
            {_toastMessage.type === 'loading' && <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" aria-hidden="true" />}
            {_toastMessage.type === 'notification' && <BellIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />}
            {_toastMessage.type === 'warning' && <ExclamationTriangleIcon className="h-6 w-6 text-orange-400" aria-hidden="true" />}
          </div>
          <div
            className={classNames(
              _toastMessage.description ? 'pt-0.5' : '',
              _toastMessage.type ? 'ml-3' : '',
              'w-0 flex-1 flex justify-between items-start',
            )}
          >
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{_toastMessage.title}</p>
              {_toastMessage.description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{_toastMessage.description}</p>}
              {_toastMessage.actions && (
                <div className="mt-3 flex space-x-7">
                  <button
                    type="button"
                    className="rounded text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Undo
                  </button>
                  <button
                    type="button"
                    className="rounded text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
            {_toastMessage.undoable && (
              <button
                type="button"
                className="ml-3 flex-shrink-0 rounded text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Undo
              </button>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="rounded inline-flex text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={wrapClick(() => toast.dismiss(_toast.id))}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LogoutToaster: FC<ToastComponentProps> = ({ toast: _toast }) => {
  const currentUser = useAuthStore(state => state.user);

  return (
    <div {..._toast.ariaProps} className="pointer-events-auto w-full max-w-sm rounded bg-white shadow-lg ring-1 ring-black ring-opacity-5">
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 pt-0.5">
            <Avatar src={currentUser?.profilePicture} alt={currentUser?.fullName || 'N A'} />
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">Logout</p>
            <p className="mt-1 text-sm text-gray-500">Are you sure you want to logout of your account?</p>
            <div className="mt-4 flex">
              <button
                type="button"
                onClick={wrapClick(() => {
                  clearAuth();
                  toast.remove(_toast.id);
                })}
                className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={wrapClick(() => toast.dismiss(_toast.id))}
                className="ml-3 inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                No
              </button>
            </div>
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              type="button"
              className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              onClick={wrapClick(() => toast.dismiss(_toast.id))}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
